import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal, Typography } from "antd";
import dayjs from "dayjs";
import { PiPrinterFill } from "react-icons/pi";

import logo from "../../../assets/images/logo_nsf.png";
import JobOrderService from "../../../service/ERP/JobOrder.service";
import { formatMoney } from "../../../utils/util";
import "./jo-invoice.css";

const joService = JobOrderService();
const ITEMS_PER_PAGE = 5;

const FALLBACK_EXPORTER = {
  company: "NINE STAR FOOD CO., LTD.",
  address: "99/9 Moo 8, Rayong, Thailand",
  phone: "-",
  email: "-",
};

const FALLBACK_BANK = {
  beneficiary: "NINE STAR FOOD CO., LTD.",
  bankName: "KASIKORNBANK PCL.",
  branch: "RAYONG",
  bankAddress: "Thailand",
  accountNo: "-",
  swiftCode: "-",
};

function buildPageRows(rows) {
  const pages = [];

  for (let index = 0; index < rows.length; index += ITEMS_PER_PAGE) {
    const chunk = rows.slice(index, index + ITEMS_PER_PAGE);
    while (chunk.length < ITEMS_PER_PAGE) {
      chunk.push({
        key: `empty-${index + chunk.length}`,
        isEmpty: true,
      });
    }
    pages.push(chunk);
  }

  return pages.length > 0 ? pages : [Array.from({ length: ITEMS_PER_PAGE }, (_, index) => ({ key: `empty-${index}`, isEmpty: true }))];
}

function InvoicePage({ exporter, consignee, invoiceMeta, pageRows, bankData, header, isLastPage, currentPage, totalPages }) {
  return (
    <section className={`jo-invoice-page${isLastPage ? " is-last-page" : " has-page-break"}`}>
      <header className="jo-invoice-header">
        <div className="jo-invoice-brand">
          <img src={logo} alt="NSF logo" className="jo-invoice-logo" />
        </div>
        <div className="jo-invoice-header-right">
          <div className="jo-invoice-title">COMMERCIAL INVOICE</div>
          <div className="jo-invoice-page-number">{currentPage}/{totalPages}</div>
        </div>
      </header>

      <section className="jo-invoice-top">
        <div className="jo-invoice-party-group">
          <div className="jo-invoice-party-row">
            <div className="jo-invoice-party-label">EXPORTER</div>
            <div className="jo-invoice-party-value">
              <Typography.Text strong>{exporter.company}</Typography.Text>
              <div>{exporter.address}</div>
              <div>Phone: {exporter.phone}</div>
              <div>E-mail: {exporter.email}</div>
            </div>
          </div>

          <div className="jo-invoice-party-row jo-invoice-party-row-consignee">
            <div className="jo-invoice-party-label">CONSIGNEE</div>
            <div className="jo-invoice-party-value">
              <Typography.Text strong>{consignee.company}</Typography.Text>
              <div>{consignee.address}</div>
              {consignee.contact ? <div>Attn: {consignee.contact}</div> : null}
              <div>Tel: {consignee.phone}</div>
              <div>E-mail: {consignee.email}</div>
            </div>
          </div>
        </div>

        <div className="jo-invoice-meta">
          <div className="jo-invoice-meta-row">
            <span>COMMERCIAL INV. NO.</span>
            <strong>{invoiceMeta.invoiceNo}</strong>
          </div>
          <div className="jo-invoice-meta-row">
            <span>DATE</span>
            <strong>{invoiceMeta.date ? dayjs(invoiceMeta.date).format("MMMM D, YYYY") : "-"}</strong>
          </div>
          <div className="jo-invoice-meta-row">
            <span>CUSTOMER ID</span>
            <strong>{invoiceMeta.customerId}</strong>
          </div>
        </div>
      </section>

      <section className="jo-invoice-summary">
        <table className="jo-invoice-summary-table">
          <thead>
            <tr>
              <th>CUSTOMER PO REF</th>
              <th>COUNTRY OF ORIGIN</th>
              <th>SHIP VIA</th>
              <th>DELIVERY TERM</th>
              <th>PAYMENT TERM</th>
              <th>LOT NO</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{invoiceMeta.customerPo}</td>
              <td>{invoiceMeta.countryOfOrigin}</td>
              <td>{invoiceMeta.shipVia}</td>
              <td>{invoiceMeta.deliveryTerm}</td>
              <td>{invoiceMeta.paymentTerm}</td>
              <td>{invoiceMeta.lotNo}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="jo-invoice-items">
        <table className="jo-invoice-items-table">
          <colgroup>
            <col style={{ width: "11%" }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>ITEM # / HS CODE</th>
              <th>PRODUCT DESCRIPTION</th>
              <th>PALLET</th>
              <th>CARTONS/PALLET</th>
              <th>PCS/CARTON</th>
              <th>TOTAL QUANTITY (BOTTLE)</th>
              <th>UNIT PRICE (USD)</th>
              <th>LINE TOTAL (USD)</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              row.isEmpty ? (
                <tr key={row.key} className="is-empty-row">
                  <td className="is-center">&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ) : (
                <tr key={row.key}>
                  <td className="is-center is-hs-code">{`#${row.itemNo} / ${row.hsCode}`}</td>
                  <td className="is-description">{row.description}</td>
                  <td className="is-center">{row.pallet}</td>
                  <td className="is-center">{formatMoney(row.cartonsPerPallet, 0)}</td>
                  <td className="is-center">{formatMoney(row.pcsPerCarton, 0)}</td>
                  <td className="is-center">{formatMoney(row.totalQuantity, 0)}</td>
                  <td className="is-right">{row.unitPrice > 0 ? `$ ${formatMoney(row.unitPrice, 2)}` : "-"}</td>
                  <td className="is-right">{row.lineTotal > 0 ? `$ ${formatMoney(row.lineTotal, 2)}` : "-"}</td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </section>

      {isLastPage && (
        <>
          <section className="jo-invoice-bank">
            <div className="jo-invoice-bank-title">BANK ACCOUNT DETAILS</div>
            <table className="jo-invoice-bank-table">
              <tbody>
                <tr>
                  <th>Beneficiary:</th>
                  <td>{bankData.beneficiary}</td>
                </tr>
                <tr>
                  <th>Bank Name:</th>
                  <td>{bankData.bankName}</td>
                </tr>
                <tr>
                  <th>Branch:</th>
                  <td>{bankData.branch}</td>
                </tr>
                <tr>
                  <th>Bank Address:</th>
                  <td>{bankData.bankAddress}</td>
                </tr>
                <tr>
                  <th>IBAN:</th>
                  <td>{bankData.accountNo}</td>
                </tr>
                <tr>
                  <th>SWIFT CODE:</th>
                  <td>{bankData.swiftCode}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <footer className="jo-invoice-footer">
            <div className="jo-invoice-thanks">THANK YOU FOR YOUR COOPERATION!</div>

            <div className="jo-invoice-footer-body">
              <div className="jo-invoice-declaration">
                {exporter.company} declares that all the information contained in this invoice to be true and correct.
              </div>
              <div className="jo-invoice-stamp-area" />
            </div>

            <div className="jo-invoice-signature-row">
              <div className="jo-invoice-signature-block">
                <div className="jo-invoice-signature-label">Date:</div>
                <div className="jo-invoice-signature-line">
                  {header?.jodate ? dayjs(header.jodate).format("DD.MM.YYYY") : dayjs().format("DD.MM.YYYY")}
                </div>
              </div>
              <div className="jo-invoice-signature-block is-wide">
                <div className="jo-invoice-signature-label">Signature and Stamp:</div>
                <div className="jo-invoice-signature-line" />
              </div>
            </div>
          </footer>
        </>
      )}
    </section>
  );
}

function JoInvoicePrintPreview() {
  const { code, version } = useParams();
  const componentRef = useRef(null);

  const [header, setHeader] = useState({});
  const [details, setDetails] = useState([]);

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;
    const previousHtmlOverflow = htmlStyle.getPropertyValue("overflow");
    const previousHtmlOverflowPriority = htmlStyle.getPropertyPriority("overflow");
    const previousBodyOverflow = bodyStyle.getPropertyValue("overflow");
    const previousBodyOverflowPriority = bodyStyle.getPropertyPriority("overflow");
    const previousHtmlOverflowY = htmlStyle.getPropertyValue("overflow-y");
    const previousHtmlOverflowYPriority = htmlStyle.getPropertyPriority("overflow-y");
    const previousBodyOverflowY = bodyStyle.getPropertyValue("overflow-y");
    const previousBodyOverflowYPriority = bodyStyle.getPropertyPriority("overflow-y");

    const applyScreenOverflow = () => {
      htmlStyle.setProperty("overflow", "auto", "important");
      htmlStyle.setProperty("overflow-y", "auto", "important");
      bodyStyle.setProperty("overflow", "auto", "important");
      bodyStyle.setProperty("overflow-y", "auto", "important");
    };

    const applyPrintOverflow = () => {
      htmlStyle.setProperty("overflow", "visible", "important");
      htmlStyle.setProperty("overflow-y", "visible", "important");
      bodyStyle.setProperty("overflow", "visible", "important");
      bodyStyle.setProperty("overflow-y", "visible", "important");
    };

    applyScreenOverflow();

    window.addEventListener("beforeprint", applyPrintOverflow);
    window.addEventListener("afterprint", applyScreenOverflow);

    return () => {
      window.removeEventListener("beforeprint", applyPrintOverflow);
      window.removeEventListener("afterprint", applyScreenOverflow);
      htmlStyle.setProperty("overflow", previousHtmlOverflow, previousHtmlOverflowPriority);
      htmlStyle.setProperty("overflow-y", previousHtmlOverflowY, previousHtmlOverflowYPriority);
      bodyStyle.setProperty("overflow", previousBodyOverflow, previousBodyOverflowPriority);
      bodyStyle.setProperty("overflow-y", previousBodyOverflowY, previousBodyOverflowYPriority);
    };
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await joService.get(code);
        const records = res?.data?.result || [];
        const selected = records?.find((item) => String(item?.version) === String(version)) || records?.[0] || {};

        setHeader(selected);
        setDetails(selected?.detail || []);
      } catch (err) {
        const message = err?.response?.data?.message || "โหลดข้อมูล Invoice ไม่สำเร็จ";
        Modal.error({
          title: "Error Request",
          content: message,
          onOk: () => window.close(),
        });
      }
    };

    init();
  }, [code, version]);

  const invoiceMeta = useMemo(() => ({
    invoiceNo: header?.invoice_no || `${header?.jocode || "JO"}-${header?.version ?? "0"}`,
    date: header?.invoice_date || header?.jodate,
    customerId: header?.cuscode || "-",
    customerPo: header?.pi_no || "-",
    countryOfOrigin: header?.country_of_origin || "THAILAND",
    shipVia: header?.shipment || "-",
    deliveryTerm: header?.delivery_term || "EXW NINE STAR FOOD FACILITY, THAILAND",
    paymentTerm: header?.payment_term || "30% IN ADVANCE + 70% BEFORE SHIPMENT",
    lotNo: header?.lot_no || header?.jocode || "-",
  }), [header]);

  const exporter = {
    company: header?.factory_name || FALLBACK_EXPORTER.company,
    address: header?.factory_address || FALLBACK_EXPORTER.address,
    phone: header?.factory_phone || FALLBACK_EXPORTER.phone,
    email: header?.factory_email || FALLBACK_EXPORTER.email,
  };

  const consignee = useMemo(() => {
    const addressParts = [
      header?.cus_delroad || header?.cus_road,
      header?.cus_delsubdistrict || header?.cus_subdistrict,
      header?.cus_deldistrict || header?.cus_district,
      header?.cus_delprovince || header?.cus_province,
      header?.cus_delzipcode || header?.cus_zipcode,
      header?.cus_delcountry || header?.cus_country,
    ].filter(Boolean);

    return {
      company: [header?.cus_prename, header?.cusname].filter(Boolean).join(" ") || "-",
      address: addressParts.length ? addressParts.join(" ") : "-",
      phone: header?.cus_tel || "-",
      email: header?.cus_email || "-",
      contact: header?.cus_contact || "",
    };
  }, [header]);

  const bankData = {
    beneficiary: header?.bank_beneficiary || FALLBACK_BANK.beneficiary,
    bankName: header?.bank_name || FALLBACK_BANK.bankName,
    branch: header?.bank_branch || FALLBACK_BANK.branch,
    bankAddress: header?.bank_address || FALLBACK_BANK.bankAddress,
    accountNo: header?.iban || header?.account_no || FALLBACK_BANK.accountNo,
    swiftCode: header?.swift_code || FALLBACK_BANK.swiftCode,
  };

  const rows = useMemo(() => {
    const totalBottle = (details || []).reduce(
      (sum, item) => sum + Number(item?.carton || 0) * Number(item?.unit_cs || 0),
      0
    );
    const totalAmount = Number(header?.total_price || 0);
    const unitPrice = totalBottle > 0 && totalAmount > 0 ? totalAmount / totalBottle : 0;

    return (details || []).map((item, index) => {
      const carton = Number(item?.carton || 0);
      const pcsPerCarton = Number(item?.unit_cs || 0);
      const totalQuantity = carton * pcsPerCarton;
      const lineTotal = unitPrice > 0 ? unitPrice * totalQuantity : 0;

      return {
        key: `${item?.code_detail || "row"}-${index}`,
        itemNo: index + 1,
        hsCode: item?.pg_code || item?.fg_code || "-",
        description: item?.pg_name || "-",
        pallet: item?.pallet || "-",
        cartonsPerPallet: carton,
        pcsPerCarton,
        totalQuantity,
        unitPrice,
        lineTotal,
      };
    });
  }, [details, header?.total_price]);

  const rowPages = useMemo(() => buildPageRows(rows), [rows]);

  return (
    <div className="jo-invoice-screen" id="jo-invoice">
      <div className="jo-invoice-toolbar">
        <Button
          className="bn-center bg-blue-400"
          onClick={() => handlePrint()}
          icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
        >
          PRINT
        </Button>
      </div>

      <div
        className="jo-invoice-preview"
        ref={componentRef}
        style={{ height: `${rowPages.length * 297}mm`, overflow: "hidden" }}
      >
        {rowPages.map((pageRows, pageIndex) => (
          <InvoicePage
            key={`invoice-page-${pageIndex}`}
            exporter={exporter}
            consignee={consignee}
            invoiceMeta={invoiceMeta}
            pageRows={pageRows}
            bankData={bankData}
            header={header}
            isLastPage={pageIndex === rowPages.length - 1}
            currentPage={pageIndex + 1}
            totalPages={rowPages.length}
          />
        ))}
      </div>
    </div>
  );
}

export default JoInvoicePrintPreview;

